import { ref, set, get, update, remove, onDisconnect } from 'firebase/database';
import { db, ensureAnonymousAuth, callDealCards, callPerformNemDraw, callScoreRound, callSubmitArrangement, callSetReady, callUpdateRules, callRejoinGame } from '../firebaseConfig';
import { ARRANGEMENT_TIMER_SECONDS } from '../gameConfig';
import { storageKey } from '../storageKeys';

// Room code generator
function generateRoomCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 5; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

function gamePath(gameId, ...rest) {
  return `games/${gameId}/${rest.join('/')}`;
}

function gameRef(gameId, ...rest) {
  return ref(db, gamePath(gameId, ...rest));
}

const gameService = {
  // === Room Management ===

  async createGame(gameMode, activeRules, displayName, avatar) {
    const uid = await ensureAnonymousAuth();

    let gameId;
    let attempts = 0;
    while (attempts < 10) {
      gameId = generateRoomCode();
      // Check meta path (has .read rule) instead of root game path (no .read rule)
      const snap = await get(gameRef(gameId, 'meta'));
      if (!snap.exists()) break;
      attempts++;
    }
    if (attempts >= 10) throw new Error('Could not generate unique room code');

    // Write meta first (sets dealerUid), then phase + player
    // Firebase rules require dealerUid to exist before writing phase
    await set(gameRef(gameId, 'meta'), {
      dealerUid: uid,
      gameMode,
      activeRules,
      deckCount: 1,
      roundNumber: 0,
      timerStartTime: null,
      timerDuration: ARRANGEMENT_TIMER_SECONDS,
      dealerConnected: true,
      roomLocked: false,
    });
    await update(ref(db), {
      [`games/${gameId}/phase`]: 'lobby',
      [`games/${gameId}/players/${uid}`]: {
        displayName, avatar, sittingOut: false, sitOutQueued: false, kicked: false,
      },
    });

    // Soft-disconnect presence is now managed reactively by whoever currently
    // holds the dealer role (see armDealerPresence / GameShell effect), so we
    // don't register a handler here that would go stale after a transfer.

    // Store session for reconnect
    localStorage.setItem(storageKey('xapxam-session'), JSON.stringify({ roomCode: gameId, uid }));

    return { gameId, uid };
  },

  async joinGame(roomCode, displayName, avatar) {
    const uid = await ensureAnonymousAuth();
    const code = roomCode.toUpperCase().trim();

    // Read meta and players separately (Firebase rules don't allow root-level reads)
    const [metaSnap, playersSnap] = await Promise.all([
      get(gameRef(code, 'meta')),
      get(gameRef(code, 'players')),
    ]);
    if (!metaSnap.exists()) return { success: false, error: 'Room not found' };

    const meta = metaSnap.val();
    if (meta?.roomLocked) return { success: false, error: 'Room is locked' };

    const existingPlayers = playersSnap.val() || {};
    // Check if rejoining
    const existingPlayer = existingPlayers[uid];
    if (existingPlayer) {
      // Rejoin is server-authoritative: `kicked` is dealer-only in rules, so the
      // CF decides — it refuses with permission-denied if the dealer kicked us.
      try {
        await callRejoinGame({ gameId: code });
      } catch (e) {
        return { success: false, error: e?.message || 'Could not rejoin room' };
      }
      localStorage.setItem(storageKey('xapxam-session'), JSON.stringify({ roomCode: code, uid }));
      return { gameId: code, uid, success: true };
    }

    // New player — sit out if game is active, mark pendingJoin for next deal
    const phaseSnap = await get(gameRef(code, 'phase'));
    const isActive = phaseSnap.val() !== 'lobby';
    await set(gameRef(code, 'players', uid), {
      displayName,
      avatar,
      sittingOut: isActive,
      sitOutQueued: false,
      // kicked omitted — dealer-only field in rules; absent == not kicked
      // BUG-5 fix: set pendingJoin so dealCards CF will deal them in next round
      ...(isActive ? { pendingJoin: true } : {}),
    });

    localStorage.setItem(storageKey('xapxam-session'), JSON.stringify({ roomCode: code, uid }));
    return { gameId: code, uid, success: true };
  },

  async leaveGame(gameId, uid) {
    const metaSnap = await get(gameRef(gameId, 'meta'));
    const meta = metaSnap.val();

    if (meta?.dealerUid === uid) {
      // Dealer leaving — mark as disbanded (other players handle succession)
      await update(ref(db, `games/${gameId}`), { disbanded: true });
    } else {
      await remove(gameRef(gameId, 'players', uid));
    }

    localStorage.removeItem(storageKey('xapxam-session'));
  },

  // === Dealing ===

  async dealCards(gameId) {
    const result = await callDealCards({ gameId });
    console.log('[DEAL] Cloud Function succeeded:', result.data);
    return result.data;
  },

  // === Arrangement ===

  async submitArrangement(gameId, uid, arrangement) {
    const result = await callSubmitArrangement({ gameId, arrangement });
    console.log('[ARRANGE] Cloud Function succeeded:', result.data);
    return result.data;
  },

  // === Ready ===

  async setReady(gameId, uid, ready, phase) {
    if (ready && phase === 'arranging') {
      // Arranging-phase ready goes through CF (validates arrangement + cards)
      const result = await callSetReady({ gameId });
      console.log('[READY] Cloud Function succeeded:', result.data);
      return result.data;
    } else if (ready) {
      // Lobby-phase ready: direct write (low-risk, no card validation needed)
      await set(gameRef(gameId, 'ready', uid), true);
    } else {
      await remove(gameRef(gameId, 'ready', uid));
    }
  },

  async setBetweenRoundsReady(gameId, uid, ready) {
    if (ready) {
      await set(gameRef(gameId, 'betweenRounds', 'ready', uid), true);
      // Un-sit-out the player when they ready up for next round
      await set(gameRef(gameId, 'players', uid, 'sittingOut'), false);
    } else {
      await remove(gameRef(gameId, 'betweenRounds', 'ready', uid));
    }
  },

  // === Nem ===

  async performNem(gameId, uid, discardedCardIds) {
    const result = await callPerformNemDraw({ gameId, discardedCardIds, targetUid: uid });
    console.log('[NEM] Cloud Function succeeded:', result.data);
    const newCards = result.data.newCards || [];
    return { newCards, success: true, usedCloudFunction: true };
  },

  async autoNem(gameId, uid) {
    // Read hand and arrangement to determine which card to auto-discard
    const [handSnap, arrSnap] = await Promise.all([
      get(gameRef(gameId, 'hands', uid)),
      get(gameRef(gameId, 'arrangements', uid)),
    ]);

    const hand = handSnap.val() || [];
    const arrangement = arrSnap.val() || { bottom: [], middle: [], top: [] };

    // Find cards in staging (not in any row)
    const placedIds = new Set([
      ...arrangement.bottom.map(c => c.id),
      ...arrangement.middle.map(c => c.id),
      ...arrangement.top.map(c => c.id),
    ]);
    const stagingCards = hand.filter(c => !placedIds.has(c.id));

    let discardCard;
    if (stagingCards.length > 0) {
      // Random from staging
      discardCard = stagingCards[Math.floor(Math.random() * stagingCards.length)];
    } else {
      // Pull from top row
      const topRow = arrangement.top || [];
      if (topRow.length === 0) {
        return { success: false, error: 'No cards to discard' };
      }

      // Determine what to pull from top row
      const rankCounts = {};
      topRow.forEach(c => { rankCounts[c.rank] = (rankCounts[c.rank] || 0) + 1; });
      const uniqueRanks = Object.keys(rankCounts);

      if (uniqueRanks.length === 2) {
        // Pair + single — pull the single
        const singleRank = uniqueRanks.find(r => rankCounts[r] === 1);
        discardCard = topRow.find(c => c.rank === singleRank);
      } else if (uniqueRanks.length === 1) {
        // Three-of-a-kind — random
        discardCard = topRow[Math.floor(Math.random() * topRow.length)];
      } else {
        // 3 different — pull lowest rank (2=lowest, A=highest)
        const rankOrder = { '2':2,'3':3,'4':4,'5':5,'6':6,'7':7,'8':8,'9':9,'10':10,'J':11,'Q':12,'K':13,'A':14 };
        const sorted = [...topRow].sort((a, b) => rankOrder[a.rank] - rankOrder[b.rank]);
        discardCard = sorted[0];
      }
    }

    const result = await gameService.performNem(gameId, uid, [discardCard.id]);
    return { discardedCard: discardCard, newCard: result.newCards?.[0], success: result.success };
  },

  // Helper to read nem completion status
  async _getNemCompleted(gameId) {
    const snap = await get(gameRef(gameId, 'nem', 'completed'));
    return snap.val() || {};
  },

  // === Timer Reset (after last Nem) ===

  async checkAndResetTimerAfterNem(gameId) {
    const [nemSnap, metaSnap, playersSnap] = await Promise.all([
      get(gameRef(gameId, 'nem', 'completed')),
      get(gameRef(gameId, 'meta')),
      get(gameRef(gameId, 'players')),
    ]);

    const nemCompleted = nemSnap.val() || {};
    const meta = metaSnap.val();
    const players = playersSnap.val() || {};

    const activeUids = Object.entries(players)
      .filter(([, p]) => !p.sittingOut && !p.kicked)
      .map(([uid]) => uid);

    const allNemDone = activeUids.every(uid => nemCompleted[uid]);
    if (!allNemDone) return;
    if (!meta?.timerStartTime) return; // unlimited hand timer — nothing to reset

    // All Nem done — reset timer to 60s if below 60s
    const elapsed = (Date.now() - meta.timerStartTime) / 1000;
    const remaining = meta.timerDuration - elapsed;

    if (remaining < 60) {
      await update(ref(db), {
        [`games/${gameId}/meta/timerStartTime`]: Date.now(),
        [`games/${gameId}/meta/timerDuration`]: 60,
      });
    }
  },

  // === Showdown ===

  async triggerShowdown(gameId) {
    console.log('[SHOWDOWN] triggerShowdown called for game:', gameId);
    const result = await callScoreRound({ gameId });
    console.log('[SHOWDOWN] Cloud Function succeeded:', result.data);
    return result.data;
  },

  async transitionToResults(gameId) {
    await set(gameRef(gameId, 'phase'), 'results');
    await set(gameRef(gameId, 'betweenRounds', 'timerStartTime'), Date.now());
  },

  async transitionToLobby(gameId) {
    await set(gameRef(gameId, 'phase'), 'lobby');
  },

  // === Player Management ===

  async toggleSitOut(gameId, uid, phase) {
    if (phase === 'arranging' || phase === 'showdown') {
      // Queue for next hand
      await set(gameRef(gameId, 'players', uid, 'sitOutQueued'), true);
    } else {
      const snap = await get(gameRef(gameId, 'players', uid, 'sittingOut'));
      await set(gameRef(gameId, 'players', uid, 'sittingOut'), !snap.val());
    }
  },

  async cancelSitOutQueue(gameId, uid) {
    await set(gameRef(gameId, 'players', uid, 'sitOutQueued'), false);
  },

  async kickPlayer(gameId, dealerUid, targetUid) {
    await update(gameRef(gameId, 'players', targetUid), { kicked: true });
  },

  // Dealer re-admits a kicked player: clears the dealer-only `kicked` flag so
  // the player's next rejoin (rejoinGame CF) is allowed again.
  async unkickPlayer(gameId, dealerUid, targetUid) {
    await update(gameRef(gameId, 'players', targetUid), { kicked: false });
  },

  // === Dealer ===

  async updateRules(gameId, ruleChanges) {
    const result = await callUpdateRules({ gameId, ruleChanges });
    console.log('[RULES] Cloud Function succeeded:', result.data);
    return result.data;
  },

  async requestDealer(gameId, uid, displayName) {
    await set(gameRef(gameId, 'dealerSuccession', 'candidate'), { uid, displayName });
    await set(gameRef(gameId, 'dealerSuccession', 'votes'), null);
  },

  async voteDealer(gameId, uid, approve) {
    await set(gameRef(gameId, 'dealerSuccession', 'votes', uid), approve);
  },

  async promoteDealer(gameId, newDealerUid) {
    await update(ref(db), {
      [`games/${gameId}/meta/dealerUid`]: newDealerUid,
      [`games/${gameId}/meta/dealerConnected`]: true,
      [`games/${gameId}/dealerSuccession`]: null,
    });
    // The new dealer's own client arms its presence handler reactively
    // (armDealerPresence) once it sees dealerUid === its uid.
  },

  async reclaimDealer(gameId, uid) {
    await update(ref(db), {
      [`games/${gameId}/meta/dealerUid`]: uid,
      [`games/${gameId}/meta/dealerConnected`]: true,
      [`games/${gameId}/dealerSuccession`]: null,
    });
  },

  // Reactive dealer presence: ONLY the client that currently holds the dealer
  // role keeps a soft-disconnect handler. A former dealer must cancel its stale
  // handler, otherwise its disconnect wrongly marks the *current* dealer as gone
  // and triggers spurious succession (dealerUid flapping).
  armDealerPresence(gameId) {
    const connRef = gameRef(gameId, 'meta', 'dealerConnected');
    onDisconnect(connRef).set(false);
    return set(connRef, true).catch(() => {});
  },

  disarmDealerPresence(gameId) {
    const connRef = gameRef(gameId, 'meta', 'dealerConnected');
    return onDisconnect(connRef).cancel().catch(() => {});
  },

  // === Reconnect ===

  getStoredSession() {
    try {
      return JSON.parse(localStorage.getItem(storageKey('xapxam-session')));
    } catch {
      return null;
    }
  },

  clearStoredSession() {
    localStorage.removeItem(storageKey('xapxam-session'));
  },

  // Wipe every client-side key a hijacked session could come back through.
  // Used by the ?fresh=1 escape path and the "Start Fresh" UI button.
  freshStart() {
    try { localStorage.removeItem(storageKey('xapxam-session')); } catch {}
    try { sessionStorage.removeItem(storageKey('xapxam_roomCode')); } catch {}
  },

  // Strict session-validity check. Optional sessionUid lets us confirm the
  // stored uid is actually a player in the room — guards against a stale
  // xapxam-session blob pointing at a room the auth user no longer belongs to
  // (disbanded, kicked, or rotated anonymous auth).
  async checkSessionValid(roomCode, sessionUid) {
    try {
      const [metaSnap, playersSnap, disbandedSnap] = await Promise.all([
        get(gameRef(roomCode, 'meta')),
        get(gameRef(roomCode, 'players')),
        get(gameRef(roomCode, 'disbanded')),
      ]);
      if (!metaSnap.exists()) return false;
      if (disbandedSnap.val() === true) return false;
      if (sessionUid) {
        const meta = metaSnap.val() || {};
        const players = playersSnap.val() || {};
        const player = players[sessionUid];
        const isDealer = meta.dealerUid === sessionUid;
        if (!isDealer && !player) return false;
        if (player?.kicked) return false;
      }
      return true;
    } catch {
      return false;
    }
  },
};

export default gameService;
